import { useCallback, useContext, useState } from "react"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { selectChats } from "@/reducers/chatsSliceSelectors"
import { XmppContext } from "@/stream/xmppClient"
import { Message } from "@/reducers/messagesSlice"
import { forwardMessage } from "@/reducers/chatsSliceThunks"
import { changeActiveChat } from "@/reducers/chatsSlice"
import { changeActiveTab, TAB_TYPES } from "@/reducers/tabsSlice"
import ChatHelpers from "@/lib/chatHelpers"

export const useMessageForwarding = (messages: Message[]) => {
  const dispatch = useAppDispatch()

  const { client, myJid } = useContext(XmppContext)
  const allChats = useAppSelector(selectChats)

  // Message forwarding state
  const [isForwardingMode, setIsForwardingMode] = useState(false)
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(
    new Set(),
  )
  const [showForwardDialog, setShowForwardDialog] = useState(false)

  // Message forwarding functions
  const enterForwardingMode = useCallback((messageIds: string[]) => {
    setIsForwardingMode(true)
    setSelectedMessageIds(new Set(messageIds))
  }, [])

  const exitForwardingMode = useCallback(() => {
    setIsForwardingMode(false)
    setSelectedMessageIds(new Set())
    setShowForwardDialog(false)
  }, [])

  const toggleMessageSelection = useCallback((messageIds: string[]) => {
    setSelectedMessageIds((prev) => {
      const newSet = new Set(prev)
      const allSelected = messageIds.every((id) => newSet.has(id))

      if (allSelected) {
        // Remove all messages
        messageIds.forEach((id) => newSet.delete(id))
      } else {
        // Add all messages
        messageIds.forEach((id) => newSet.add(id))
      }

      return newSet
    })
  }, [])

  const handleForwardMessages = useCallback(() => {
    if (selectedMessageIds.size > 0) {
      setShowForwardDialog(true)
    }
  }, [selectedMessageIds.size])

  const handleForwardToChat = useCallback(
    (targetChatJid: string) => {
      // Find the target chat
      try {
        const targetChat = allChats.find((c) => c.jid === targetChatJid)
        if (!targetChat) {
          console.error("Target chat not found:", targetChatJid)
          exitForwardingMode()
          return
        }

        // Get the messages to forward
        const messagesToForward = messages.filter((message) =>
          selectedMessageIds.has(message.id),
        )

        if (messagesToForward.length === 0) {
          exitForwardingMode()
          return
        }

        // Forward each selected message to the target chat with proper forwarded stanza
        messagesToForward.forEach((message) => {
          // Use the forwardMessage thunk to send with proper forwarded metadata
          dispatch(
            forwardMessage(message, client, myJid.toString(), targetChat),
          )
          dispatch(changeActiveChat({ activeChatJid: targetChat.jid }))
          if (targetChat.jid.startsWith("cmr")) {
            dispatch(changeActiveTab({ activeTab: TAB_TYPES.CMR }))
          } else if (ChatHelpers.isChatWithSlickAI(targetChat.jid)) {
            dispatch(changeActiveTab({ activeTab: TAB_TYPES.SLICK_AI }))
          } else {
            dispatch(changeActiveTab({ activeTab: TAB_TYPES.CHATS }))
          }
          exitForwardingMode()
        })
      } catch (error) {
        console.log(error)
        exitForwardingMode()
      }
    },
    [
      selectedMessageIds,
      messages,
      allChats,
      exitForwardingMode,
      dispatch,
      client,
      myJid,
    ],
  )

  const handleCloseForwardDialog = useCallback(() => {
    setShowForwardDialog(false)
  }, [])

  return {
    // State
    isForwardingMode,
    selectedMessageIds,
    showForwardDialog,

    // Actions
    enterForwardingMode,
    exitForwardingMode,
    toggleMessageSelection,
    handleForwardMessages,
    handleForwardToChat,
    handleCloseForwardDialog,
  }
}
